<template>
	<div class="overlay-dialog">
		<div
			v-if="!displayConfirm"
		>
			<h2 class="overlay-dialog__title" v-html="$t('cart.timeEdit.title')"/>
			<div class="overlay-dialog__text">
				<p v-html="$t('cart.timeEdit.text')" />
				<Button
					@click.prevent="$emit('close')"
					class="overlay-dialog__text-button"
					level="tertiary"
					type="button"
					:text="$t('cart.timeEdit.cancelButton')"
					arrow
					reverse
				/>
			</div>
			<GeneralMessage
				v-if="errorMessage"
				:message="errorMessage"
				type="error"
				@close="errorMessage = ''"
			/>
			<OrderBoxTime
				v-if="locationOrderMinDate && locationOrderMaxDate"
				class="order-box__time--overlay"
				:order-now="locationOrderNow"
				:order-now-available="cartOrderNowAvailable"
				:date="selectedDate"
				:dateMin="locationOrderMinDate.format('L')"
				:dateMax="locationOrderMaxDate.format('L')"
				:isDateDisabled="isDateDisabled.bind(this)"
				:time="selectedTime"
				:times="locationOrderTimes"
				:buttonText="$t('cart.timeEdit.acceptButton')"
				:yielding-messages="yieldingMessages"
				@ordernowchanged="onLocationOrderNowChange"
				@datechanged="onLocationDateSelect"
				@timechanged="onLocationTimeSelect"
				@click="onTimeChange()"
			/>
		</div>
		<div
			v-else
		>
			<h2 class="overlay-dialog__title" v-html="$t('cart.timeEdit.cartChange.title')" />
			<div class="overlay-dialog__text">
				<p v-html="$t('cart.timeEdit.cartChange.text')" />
			</div>

			<CartChanges
				:text="$t('cart.timeEdit.cartChange.changeTitle')"
				:changes="cartChangedItems"
			/>

			<div class="overlay-dialog__buttons">
				<Button
					@click.prevent="$emit('close')"
					level="tertiary"
					type="button"
					:text="$t('cart.timeEdit.cartChange.cancelButton')"
					arrow
					reverse
				/>
				<Button
					@click.prevent="onTimeChange(true)"
					level="primary"
					type="button"
					:text="$t('cart.timeEdit.cartChange.acceptButton')"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Button from '@/components/Button'
import OrderBoxTime from '@/components/OrderBoxTime';
import CartChanges from '@/components/CartChanges';
import GeneralMessage from '@/components/GeneralMessage';
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"

export default {
	mixins: [

		],
	components: {
		Button,
		OrderBoxTime,
		CartChanges,
		GeneralMessage,
	},
	props: {

	},
	data() {
		return {
			form: {
				selectedDate: null,
				selectedTime: null,
			},
			date: {
				min: '',
				max: '',
			},
			displayConfirm: false,
			errorMessage: "",
		}
	},
	computed: {
		...mapGetters("cart", [
			"cartDeliveryTime",
			"shippingMethod",
			"shopLocation",
			"cartChangedItems",
			"cartOrderNow",
			"cartOrderNowAvailable",
			"deliveryTime"
		]),
		...mapGetters("location", [
			"locationOrderDate",
			"locationOrderTime",
			"locationOrderMinDate",
			"locationOrderMaxDate",
			"locationOrderDisabledDates",
			"locationOrderTimes",
			"locationOrderNow",
			"locationYieldingMessages",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		selectedDate: {
			get () {
				return this.locationOrderDate
			},
			set (value) {
				this.$store.dispatch('location/setOrderDate', { dateStr: value })
			}
		},
		selectedTime: {
			get () {
				return this.locationOrderTime
			},
			set (value) {
				this.$store.dispatch('location/setOrderTime', { timeStr: value })
			}
		},
		yieldingMessages() {
			return this.locationYieldingMessages
				.filter(m => !m.type || m.type == this.selectedShippingMethod)
				.map(m => m.content)
		}
	},
	methods: {
		onLocationOrderNowChange(value) {
			this.$store.dispatch('location/setOrderNow', { value })
		},
		onLocationDateSelect(date) {
			this.selectedDate = date;
			if (this.selectedDate) {
				this.selectedTime = this.locationOrderTimes[0];
			}
		},
		onLocationTimeSelect(time) {
			this.selectedTime = time;
		},
		isDateDisabled(date) {
			const dates = this.locationOrderDisabledDates
			const check = dayjsLocal(date)
			return dates.find(d => isSameDay(d, check))
		},
		async onTimeChange(confirm = false) {
			let validDateTime = (this.locationOrderNow && this.locationOrderNowAvailable) || (this.selectedDate && this.selectedTime)
			if (!validDateTime) {
				this.errorMessage = this.$t('cart.timeEdit.errorTime');
				return
			}

			this.loading = true
			try {
				this.errorMessage = ''
				await this.$store.dispatch('updateDeliverTime', { orderNow: this.locationOrderNow && this.cartOrderNowAvailable, date: this.selectedDate, time: this.selectedTime, confirmChange: confirm })
				this.$emit('change', this.form)
			} catch (err) {
				if (err.response && err.response.data && err.response.data.notAvailableItems) {
					this.displayConfirm = true
				} else if (err.response && err.response.data && err.response.data.message) {
					this.errorMessage = err.response.data.errors && err.response.data.errors.deliverTime
						? err.response.data.errors.deliverTime.join()
						: err.response.data.message
				} else {
					// throw err
					this.$store.dispatch('dialog/showDialog', {
						title: this.$t('forms.errors.error'),
						message: this.$t('forms.errors.generic'),
						confirm: 'OK',
					});
				}
			}
			this.loading = false
		}
	},
	created() {
		this.loading = true;
	},
	async mounted() {
		this.errorMessage = ''
		this.loading = true

		try {
			await this.$store.dispatch('cart/load')

			this.$store.dispatch('location/setLocation', {
				location: this.shopLocation,
				deliveryMethod: this.shippingMethod.code,
				deliverTime: this.cartDeliveryTime,
				canOrderNow: this.cartOrderNowAvailable,
				deliveryTime: this.deliveryTime,
			})

			this.$store.dispatch('location/setOrderNow', { value: this.cartOrderNow })
		} catch (err) {
			this.isDebug() && console.error("cart is not valid anymore", err)
		}

		this.loading = false
	}
}
</script>

<style>

</style>
